import { useEffect } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import { clarityOneLoginUrlWithRedirect } from "@shared/clarityOneAuthRedirect";
import { formatDate } from "@/lib/dateUtils";
import { Loader2 } from "lucide-react";

function formatTxnDate(value: string | null | undefined): string {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      return formatDate(value);
    } catch {
      return value.slice(0, 10);
    }
  }
  return value;
}

function serviceStatusClass(status: string): string {
  const s = status.toLowerCase();
  if (s === "closed" || s === "report_sent" || s === "repair_complete") {
    return "bg-[#E8F5E9] text-[#2E7D32]";
  }
  if (s === "in_progress" || s === "pending_parts" || s === "diagnosis" || s === "pending_review") {
    return "bg-[#FFF4E5] text-[#B8956E]";
  }
  if (s === "scheduled" || s === "received") {
    return "bg-[#1A2744]/10 text-[#1A2744]";
  }
  if (s === "waived") return "bg-[#F3E8FF] text-[#6B21A8]";
  return "bg-[#F0EDE6] text-[#6B7280]";
}

export default function ClarityOneServicePage() {
  const [, setLocation] = useLocation();
  const { data: me, isLoading: meLoading } = trpc.clarityOneAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const { data, isLoading, error } = trpc.clarityOneRecords.listServiceHistory.useQuery(undefined, {
    enabled: !!me,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!meLoading && !me) setLocation(clarityOneLoginUrlWithRedirect("/service"));
  }, [meLoading, me, setLocation]);

  if (meLoading || !me) {
    return (
      <ClarityOneLayout showNav={false}>
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#B8956E]" />
        </div>
      </ClarityOneLayout>
    );
  }

  return (
    <ClarityOneLayout showNav>
      <h1 className="text-3xl font-light text-[#1A2744] tracking-tight mb-2">Service</h1>
      <p className="text-sm text-[#8B7355] mb-10 max-w-xl leading-relaxed">
        Service visits and repair orders for your account.
      </p>

      {error ? (
        <p className="text-sm text-[#8B7355]">We could not load your service history. Please try again.</p>
      ) : null}

      {isLoading ? (
        <Loader2 className="h-6 w-6 animate-spin text-[#B8956E]" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="clarity-one-service">
          <div className="bg-white rounded-2xl border border-[#E8E4DC] px-5 py-5">
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#B8956E] mb-4">
              Service Visits
            </p>
            {!data?.serviceVisits.length ? (
              <p className="text-sm text-[#8B7355]" data-testid="service-svr-empty">
                No service visits yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className="w-full min-w-[420px] text-left text-sm"
                  data-testid="service-svr-table"
                >
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-[#B8956E] border-b border-[#E8E4DC]">
                      <th className="py-2 pr-3 font-medium">Date</th>
                      <th className="py-2 pr-3 font-medium">SVR Reference</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.serviceVisits.map((v) => (
                      <tr key={v.id} className="border-b border-[#F0EDE6]">
                        <td className="py-3 pr-3 text-[#1A2744]">{formatTxnDate(v.date)}</td>
                        <td className="py-3 pr-3 text-[#1A2744]">{v.reference}</td>
                        <td className="py-3 pr-3">
                          <span
                            className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${serviceStatusClass(v.status)}`}
                          >
                            {v.statusLabel}
                          </span>
                        </td>
                        <td className="py-3 text-[#8B7355]">—</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-[#E8E4DC] px-5 py-5">
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#B8956E] mb-4">
              Repair Orders
            </p>
            {!data?.repairOrders.length ? (
              <p className="text-sm text-[#8B7355]" data-testid="service-ro-empty">
                No repair orders yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className="w-full min-w-[420px] text-left text-sm"
                  data-testid="service-ro-table"
                >
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-[#B8956E] border-b border-[#E8E4DC]">
                      <th className="py-2 pr-3 font-medium">Date</th>
                      <th className="py-2 pr-3 font-medium">RO Reference</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.repairOrders.map((r) => (
                      <tr key={r.id} className="border-b border-[#F0EDE6]">
                        <td className="py-3 pr-3 text-[#1A2744]">{formatTxnDate(r.date)}</td>
                        <td className="py-3 pr-3 text-[#1A2744]">{r.reference}</td>
                        <td className="py-3 pr-3">
                          <span
                            className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${serviceStatusClass(r.status)}`}
                          >
                            {r.statusLabel}
                          </span>
                        </td>
                        <td className="py-3 text-[#8B7355]">—</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </ClarityOneLayout>
  );
}
